import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useStatusStore } from '@/stores/status.store';
import { setMyStatus, clearMyStatus } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import { STATUS_PRESETS, DND_DURATION_PRESETS } from '@goodchat/shared/constants';

interface SetStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SetStatusDialog({ open, onOpenChange }: SetStatusDialogProps) {
  const myStatus = useStatusStore((s) => s.myStatus);
  const setMyStatusStore = useStatusStore((s) => s.setMyStatus);
  const [emoji, setEmoji] = useState(myStatus?.emoji ?? '');
  const [text, setText] = useState(myStatus?.text ?? '');
  const [clearAfterHours, setClearAfterHours] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Re-sync local state from myStatus when dialog opens
  useEffect(() => {
    if (open) {
      setEmoji(myStatus?.emoji ?? '');
      setText(myStatus?.text ?? '');
      setClearAfterHours(null);
    }
  }, [open, myStatus]);

  const handleSave = async () => {
    if (!text.trim()) return;
    setIsSubmitting(true);
    try {
      const expiresAt = clearAfterHours
        ? new Date(Date.now() + clearAfterHours * 60 * 60 * 1000).toISOString()
        : undefined;
      const res = await setMyStatus({
        emoji: emoji || undefined,
        text: text.trim(),
        expiresAt,
      });
      setMyStatusStore({
        emoji: res.status.emoji,
        text: res.status.text,
        expiresAt: res.status.expiresAt,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to set status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    setIsSubmitting(true);
    try {
      await clearMyStatus();
      setMyStatusStore(null);
      setEmoji('');
      setText('');
      setClearAfterHours(null);
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to clear status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyPreset = (preset: { emoji: string; text: string }) => {
    setEmoji(preset.emoji);
    setText(preset.text);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <Dialog.Title className="text-lg font-semibold">Set Status</Dialog.Title>

          <div className="mt-4 space-y-4">
            {/* Presets */}
            <div className="flex flex-wrap gap-2">
              {STATUS_PRESETS.map((preset) => (
                <button
                  key={preset.text}
                  onClick={() => applyPreset(preset)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm transition-colors hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  {preset.emoji} {preset.text}
                </button>
              ))}
            </div>

            {/* Emoji + Text inputs */}
            <div className="flex gap-2">
              <input
                type="text"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="Emoji"
                className="w-16 rounded-lg border border-gray-300 px-2 py-2 text-center text-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                maxLength={4}
              />
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's your status?"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                maxLength={200}
              />
            </div>

            {/* Clear after */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Clear after
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setClearAfterHours(null)}
                  className={`rounded-lg border px-3 py-1.5 text-sm ${
                    clearAfterHours === null
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                      : 'border-gray-200 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800'
                  }`}
                >
                  Don't clear
                </button>
                {DND_DURATION_PRESETS.map((h) => (
                  <button
                    key={h}
                    onClick={() => setClearAfterHours(h)}
                    className={`rounded-lg border px-3 py-1.5 text-sm ${
                      clearAfterHours === h
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                        : 'border-gray-200 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            {myStatus && (
              <button
                onClick={handleClear}
                disabled={isSubmitting}
                className="rounded-lg px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950"
              >
                Clear status
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <Dialog.Close className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
                Cancel
              </Dialog.Close>
              <button
                onClick={handleSave}
                disabled={isSubmitting || !text.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
